import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { EmptyValues } from '@app/shared/constants/empty-values';
import { NavigationItem } from '@app/shared/components/secondary-navigation/secondary-navigation-types';
import { SecondaryNavigation } from '@app/shared/components/secondary-navigation/SecondaryNavigation';
import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { SecondaryNavigationVariation } from '@app/shared/components/secondary-navigation/secondary-navigation-enums';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import PwcNavigate from '@app/shared/routing/PwcNavigate';
import { setCapitalStructureDividendsSelectedCase } from '@app/core/store/ui-values-slice';
import { enumKeyByValue } from '@app/shared/helpers';

export const DividendNavigationControls = () => {
  const { dividendSetId } = useParams();
  const project = useAppSelector((state) => state.project.projectDraft);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const usesPwerm =
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM_AND_OPM) ||
    project.details.calcMethod === enumKeyByValue(CalcMethod, CalcMethod.PWERM);
  const dispatch = useAppDispatch();

  const dividendSetIdRef = useRef(dividendSetId);

  useEffect(() => {
    dividendSetIdRef.current = dividendSetId;
  }, [dividendSetId]);

  useEffect(() => {
    return () => {
      if (dividendSetIdRef.current !== undefined) {
        dispatch(setCapitalStructureDividendsSelectedCase(dividendSetIdRef.current));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storedDividendSetId = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.dividends.selectedCase
  );
  const lastPwermCase = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.dividends.lastPwermCase
  );

  const forecastDividendSets = capitalStructure.forecastDividendSets;

  const forecastDividendSetsWithCase = Object.fromEntries(
    Object.entries(forecastDividendSets)
      .filter(([setId]) => {
        if (usesPwerm) {
          return project.pwermInput.cases.some((c) => c.dividendSetId === setId);
        }
        return false;
      })
      .map(([setId, set]) => {
        const caseObj = project.pwermInput.cases.find((c) => c.dividendSetId === setId);
        return [
          setId,
          {
            ...set,
            selectedCase: caseObj?.narrative ?? null,
            order: caseObj?.caseOrder ?? 0,
          },
        ];
      })
  );

  const navigationItems: NavigationItem[] = useMemo(() => {
    const items =
      Object.values(forecastDividendSetsWithCase)
        .map((item) => ({
          label: item.selectedCase ?? EmptyValues.EnDash,
          route: ':dividendSetId',
          params: { dividendSetId: item.dividendSetId },
          order: item.order,
          isHighlighted: item.dividendSetId === dividendSetId,
        }))
        .sort((a, b) => a.order! - b.order!) || [];

    return items;
  }, [forecastDividendSetsWithCase, dividendSetId]);

  if (!usesPwerm) {
    if (dividendSetId !== '') {
      return <PwcNavigate route="" params={{}} />;
    }
    return null;
  }

  const preferredDividendSetId =
    lastPwermCase && forecastDividendSetsWithCase[lastPwermCase]
      ? lastPwermCase
      : storedDividendSetId;

  if (
    preferredDividendSetId &&
    !dividendSetId &&
    forecastDividendSetsWithCase[preferredDividendSetId]
  ) {
    return (
      <PwcNavigate route=":dividendSetId" params={{ dividendSetId: preferredDividendSetId }} />
    );
  } else if (!dividendSetId && Object.keys(forecastDividendSetsWithCase).length > 0) {
    const firstKey = Object.keys(forecastDividendSetsWithCase)
      .map((key) => ({
        key,
        order: forecastDividendSetsWithCase[key].order,
      }))
      .sort((a, b) => a.order - b.order)[0]?.key;

    const firstDividendSetId = forecastDividendSetsWithCase[firstKey].dividendSetId;

    return <PwcNavigate route=":dividendSetId" params={{ dividendSetId: firstDividendSetId }} />;
  }

  return (
    <SecondaryNavigation
      navigationItems={navigationItems}
      variation={SecondaryNavigationVariation.Scrollable}
    />
  );
};
